import { RoutingTabs } from '#app/components/RoutingTabs/RoutingTabs.js';
import { useMount } from '@contra/react-hooks/useMount';
import { useRef, useState } from 'react';
import { css, styled } from 'styled-components';

const Wrapper = styled.div`
  ${({ theme }) => css`
    overflow: hidden;
    position: relative;
    width: 100vw;
    margin: 0 -16px;

    ${theme.mediaQueries.md} {
      width: 100%;
      margin: unset;
      padding: unset;
    }
  `}
`;

const StyledRoutingTabsScroll = styled(RoutingTabs.Scroll)<{
  $isOverflowing: boolean;
}>`
  ${({ $isOverflowing = false, theme }) => css`
    margin: 0 0 40px 0;
    padding: 0 0 0 16px;
    box-shadow: none;
    position: relative;
    overflow-x: auto;
    overflow-y: hidden;
    width: 100vw;

    & > ul {
      min-height: 36px;

      ${theme.mediaQueries.md} {
        min-height: 42px;
      }

      li:last-of-type {
        padding-right: 32px;
      }
    }

    ${theme.mediaQueries.md} {
      margin: unset;
      padding: unset;
      margin-top: 40px;
      margin-bottom: 40px;
      width: calc(100% + ${$isOverflowing ? '32px' : '0px'});
    }

    ${theme.mediaQueries.xl} {
      width: 100%;
    }
  `}
`;

const Fades = styled.div<{
  $hasScrolled: boolean;
  $isOverflowing: boolean;
}>`
  ${({ $hasScrolled, $isOverflowing, theme }) =>
    $isOverflowing &&
    css`
      ${theme.mediaQueries.md} {
        &::before,
        &::after {
          content: '';
          position: absolute;
          top: 0;
          bottom: 0;
          width: 32px;
          pointer-events: none;
          z-index: 1;
        }

        &::before {
          left: 0;
          background: linear-gradient(
            to right,
            ${$hasScrolled ? 'white' : 'rgba(255, 255, 255, 0)'} 0%,
            rgba(255, 255, 255, 0) 100%
          );
          opacity: ${$hasScrolled ? 1 : 0};
          transition: opacity 0.3s ease;
        }

        &::after {
          right: 0;
          background: linear-gradient(
            to left,
            white 0%,
            rgba(255, 255, 255, 0) 100%
          );
        }
      }
    `}
`;

export const ProfileRoutingTabs = ({ children, ...rest }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);

  const checkOverflow = () => {
    if (containerRef.current) {
      const { clientWidth, scrollWidth } = containerRef.current;
      setIsOverflowing(scrollWidth > clientWidth);
    }
  };

  useMount(() => {
    checkOverflow();
    window.addEventListener('resize', checkOverflow);

    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  });

  const handleScroll = () => {
    if (containerRef.current) {
      setHasScrolled(containerRef.current.scrollLeft > 0);
    }
  };

  useMount(() => {
    const ref = containerRef.current;

    if (ref) {
      ref.addEventListener('scroll', handleScroll);
      return () => ref.removeEventListener('scroll', handleScroll);
    }

    return undefined;
  });

  return (
    <Wrapper>
      <Fades
        $hasScrolled={hasScrolled}
        $isOverflowing={isOverflowing}
      />

      <StyledRoutingTabsScroll
        $isOverflowing={isOverflowing}
        ref={containerRef}
        {...rest}
      >
        {children}
      </StyledRoutingTabsScroll>
    </Wrapper>
  );
};
